import React, { useEffect } from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { useQuery } from "@tanstack/react-query";

import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseTextField from "../../../components/ui/BaseTextField";

import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { showToast } from "../../../store/toast/toastSlice";

import {
  createSubCategory,
  fetchCategories,
  fetchVendors,
} from "../../../services/api/category";

const validationSchema = yup.object({
  categoryId: yup.string("").required("Category is required"),
  vendorId: yup.string("").required("Category is required"),
  productName: yup.string().trim().required("Skill is required"),
});

export default function AddEditProductModal({
  open,
  close,
  mode = "add",
  initialData = null,
  refetchProducts,
}) {
  const isEdit = mode === "edit";
  const dispatch = useDispatch();

  const {
    isPending: isCreateProductLoading,
    error: createProductError,
    mutateAsync: createProductMutation,
  } = useMutation({ mutationFn: createSubCategory });

  const handleCreateProduct = async () => {
    try {
      await createProductMutation({
        vendorId: formik?.values?.vendorId,
        subCategory: formik?.values?.productName.trim(),
      });
      dispatch(showToast({ message: "Skill created", type: "success" }));
      refetchProducts();
      formik.resetForm();
      close(false);
    } catch (e) {}
  };

  const formik = useFormik({
    initialValues: { categoryId: null, vendorId: null, productName: "" },
    validationSchema,
    onSubmit: handleCreateProduct,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const { data: categoriesRes } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: vendorsRes } = useQuery({
    queryKey: ["vendorsData", formik?.values.categoryId],
    queryFn: () => fetchVendors(formik?.values.categoryId),
    enabled: !!formik?.values.categoryId,
  });

  const categories =
    categoriesRes?.data?.map((c) => ({
      label: c.category ?? c.name,
      value: c.id,
    })) ?? [];

  const vendors =
    vendorsRes?.data?.map((v) => ({ label: v.name, value: v.id })) ?? [];

  useEffect(() => {
    if (!open) return;
    formik.setValues(
      {
        categoryId: initialData?.categoryId ?? null,
        vendorId: initialData?.vendorId ?? null,
        productName: initialData?.productNames ?? "",
      },
      false
    );
  }, [open, initialData]);

  useEffect(() => {
    if (!formik.values.categoryId) {
      formik.setFieldValue("vendorId", null, false);
    }
  }, [formik.values.categoryId]);

  return (
    <form>
      <BaseModal
        className="-top-[200px] overflow-auto"
        open={open}
        close={close}
      >
        <div className="py-7 px-12 xl:w-[1000px] lg:w-[900px] sm:w-[700px]">
          <h6 className="text-dark font-normal border-b pb-1">
            {isEdit ? "Edit Skill" : "Create New Skill"}
          </h6>

          <div className="grid grid-cols-2 gap-x-8 mt-4">
            <BaseAutoSelect
              className="mb-3"
              placeholder="Select technology stack"
              fullHeight={false}
              required
              labelShrink={false}
              label={<span className="font-semibold">Technology Stack Name</span>}
              options={categories}
              id="categoryId"
              name="categoryId"
              isDefaultField={false}
              defaultValue={formik.values.categoryId}
              value={formik.values.categoryId}
              onChange={(e) => {
                formik.handleChange(e);
                formik.setFieldValue("vendorId", null, false);
              }}
              onBlur={formik.handleBlur}
              error={
                formik.touched.categoryId && Boolean(formik.errors.categoryId)
              }
              helperText={formik.touched.categoryId && formik.errors.categoryId}
            />

            <BaseAutoSelect
              className="mb-3"
              placeholder="Select category"
              fullHeight={false}
              required
              labelShrink={false}
              label={<span className="font-semibold">Category Name</span>}
              options={vendors}
              id="vendorId"
              name="vendorId"
              isDefaultField={false}
              defaultValue={formik.values.vendorId}
              value={formik.values.vendorId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.vendorId && Boolean(formik.errors.vendorId)}
              helperText={formik.touched.vendorId && formik.errors.vendorId}
            />

            <div className="col-span-2">
              <p className="font-semibold mb-2">Skill Name</p>
              <BaseTextField
                id="productName"
                name="productName"
                placeholder="e.g., Cable Tape"
                value={formik.values.productName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.productName &&
                  Boolean(formik.errors.productName)
                }
                helperText={
                  formik.touched.productName && formik.errors.productName
                }
              />
            </div>
          </div>
        </div>

        <div
          className="d-flex content-center bg-white py-6 mt-6"
          style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
        >
          <BaseButton
            className="px-[28px] py-[5px] bg-light text-primary !hover:text-primary"
            variant="outlined"
            color="primary"
            style={{ marginRight: "16px" }}
            onClick={() => close(false)}
          >
            Cancel
          </BaseButton>

          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            className="px-[12px] py-[5px]"
            color="primary"
            onClick={formik.handleSubmit}
            disabled={
              !formik.values.categoryId ||
              !formik.values.vendorId ||
              !formik.values.productName.trim()
            }
          >
            {isEdit ? "Save" : "Create"}
          </BaseButton>
        </div>
      </BaseModal>
    </form>
  );
}
